import React, { useEffect, useState, useCallback } from "react";
import { History, Check, Loader2, Wrench, Package, DollarSign, Mail } from "lucide-react";
import { COLOR } from "./pillars/_cards";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * BatteryHistoryCard — owner-editable battery facts Tesla's Fleet API
 * can't report: Battery Repaired?, Battery Replaced?, Battery Cycles
 * override, and the home $/kWh preset used by the Charging ledger.
 *
 * Stored per-VIN in db.battery_history and persisted to every future
 * EVIQ Battery Passport™ so buyers see the honest record.
 */
export const BatteryHistoryCard = ({ vehicle }) => {
  const vin = vehicle?.vin || null;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!vin) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/battery_history/${encodeURIComponent(vin)}`);
      if (r.ok) setData(await r.json());
    } catch (_e) {}
    finally { setLoading(false); }
  }, [vin]);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    if (!vin) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE}/api/battery_history/${encodeURIComponent(vin)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (r.ok) setData(await r.json());
    } catch (_e) {}
    finally { setSaving(false); }
  };

  const d = data || {};
  const chipTone = (yes) => (yes ? COLOR.amber : COLOR.green);

  return (
    <div
      className="rounded-xl border p-3"
      style={{ borderColor: "rgba(0,229,255,0.28)", background: "rgba(0,229,255,0.03)" }}
      data-testid="care-battery-history-card"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 text-left"
        data-testid="care-battery-history-toggle"
      >
        <div
          className="w-8 h-8 rounded-md flex items-center justify-center border shrink-0"
          style={{ background: "rgba(0,229,255,0.14)", borderColor: "rgba(0,229,255,0.45)" }}
        >
          <History className="w-4 h-4" style={{ color: COLOR.cyan }} strokeWidth={2.4} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
            Battery history
          </div>
          <div className="text-[13px] text-white font-semibold leading-tight">
            {loading ? "Loading…" : "Facts we can't detect from telemetry"}
          </div>
          <div className="font-mono text-[10px] text-zinc-400 leading-tight mt-0.5">
            <span style={{ color: chipTone(d.battery_repaired) }}>
              Repaired · {d.battery_repaired ? "Yes" : "No"}
            </span>
            <span className="mx-1.5 text-zinc-600">·</span>
            <span style={{ color: chipTone(d.battery_replaced) }}>
              Replaced · {d.battery_replaced ? "Yes" : "No"}
            </span>
            {typeof d.battery_cycles_override === "number" && (
              <>
                <span className="mx-1.5 text-zinc-600">·</span>
                Cycles override: {d.battery_cycles_override}
              </>
            )}
          </div>
        </div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">
          {expanded ? "Hide" : "Edit"}
        </div>
      </button>

      {expanded && (
        <div className="mt-3 border-t border-white/5 pt-3 space-y-3" data-testid="care-battery-history-editor">
          <Row
            testid="hist-repaired"
            icon={Wrench}
            label="Was the battery ever repaired?"
            hint="e.g. contactor replacement, coolant hose recall"
            value={!!d.battery_repaired}
            date={d.battery_repaired_date}
            note={d.battery_repaired_note}
            onSave={(v) => save({ battery_repaired: v.checked, battery_repaired_date: v.date || null, battery_repaired_note: v.note || null })}
          />
          <Row
            testid="hist-replaced"
            icon={Package}
            label="Was the battery ever fully replaced?"
            hint="Rare, but resets the pack's history"
            value={!!d.battery_replaced}
            date={d.battery_replaced_date}
            note={d.battery_replaced_note}
            onSave={(v) => save({ battery_replaced: v.checked, battery_replaced_date: v.date || null, battery_replaced_note: v.note || null })}
          />
          <CyclesRow
            value={d.battery_cycles_override}
            onSave={(n) => save({ battery_cycles_override: n })}
          />
          <PriceRow
            value={d.home_price_per_kwh_usd}
            onSave={(n) => save({ home_price_per_kwh_usd: n })}
          />
          <OwnerEmailRow
            value={d.owner_email}
            notifiedAt={d.legendary_notified_at}
            onSave={(email) => save({ owner_email: email })}
          />
          {saving && (
            <div className="flex items-center gap-2 text-[11px] text-zinc-500">
              <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2.2} />
              Saving…
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Row = ({ testid, icon: Icon, label, hint, value, date, note, onSave }) => {
  const [checked, setChecked] = useState(!!value);
  const [dt, setDt] = useState(date || "");
  const [nt, setNt] = useState(note || "");
  useEffect(() => { setChecked(!!value); setDt(date || ""); setNt(note || ""); }, [value, date, note]);
  const dirty = checked !== !!value || (dt || "") !== (date || "") || (nt || "") !== (note || "");
  return (
    <div className="rounded-md border p-2.5" style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }} data-testid={testid}>
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
          data-testid={`${testid}-checkbox`}
          className="mt-0.5 shrink-0 w-3.5 h-3.5 accent-[#00E5FF]"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[12px] text-white">
            <Icon className="w-3 h-3 shrink-0" style={{ color: COLOR.cyan }} strokeWidth={2.4} />
            {label}
          </div>
          {hint && <div className="text-[10px] text-zinc-500 leading-snug mt-0.5">{hint}</div>}
        </div>
      </label>
      {checked && (
        <div className="grid grid-cols-2 gap-2 mt-2 pl-5">
          <input
            type="date"
            value={dt}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setDt(e.target.value)}
            data-testid={`${testid}-date`}
            className="rounded-md border border-white/10 bg-black/60 px-2 py-1 text-[11px] text-white focus:outline-none focus:border-[#00E5FF]/60 focus:ring-1 focus:ring-[#00E5FF]/30"
          />
          <input
            type="text"
            value={nt}
            maxLength={200}
            onChange={(e) => setNt(e.target.value)}
            placeholder="Note (shop, part #)"
            data-testid={`${testid}-note`}
            className="rounded-md border border-white/10 bg-black/60 px-2 py-1 text-[11px] text-white placeholder-zinc-600 focus:outline-none focus:border-[#00E5FF]/60 focus:ring-1 focus:ring-[#00E5FF]/30"
          />
        </div>
      )}
      {dirty && (
        <div className="mt-2 flex justify-end pl-5">
          <button
            type="button"
            onClick={() => onSave({ checked, date: dt || null, note: nt || null })}
            data-testid={`${testid}-save`}
            className="inline-flex items-center gap-1 rounded-md brand-gradient text-black px-2 py-1 text-[10px] font-mono uppercase tracking-wider font-semibold hover:opacity-90"
          >
            <Check className="w-3 h-3" strokeWidth={2.4} />
            Save
          </button>
        </div>
      )}
    </div>
  );
};

const CyclesRow = ({ value, onSave }) => {
  const [v, setV] = useState(value == null ? "" : String(value));
  useEffect(() => { setV(value == null ? "" : String(value)); }, [value]);
  const dirty = (v || "") !== (value == null ? "" : String(value));
  return (
    <div className="rounded-md border p-2.5" style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }} data-testid="hist-cycles">
      <div className="text-[12px] text-white flex items-center gap-1.5">
        <History className="w-3 h-3 shrink-0" style={{ color: COLOR.cyan }} strokeWidth={2.4} />
        Battery cycles override (optional)
      </div>
      <div className="text-[10px] text-zinc-500 leading-snug mt-0.5 mb-1.5">
        Auto-derived from odometer ÷ EPA range. Override only if you have exact BMS data.
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={v}
          min="0"
          max="50000"
          placeholder="auto"
          onChange={(e) => setV(e.target.value)}
          data-testid="hist-cycles-input"
          className="flex-1 rounded-md border border-white/10 bg-black/60 px-2 py-1 text-[11px] text-white placeholder-zinc-600 focus:outline-none focus:border-[#00E5FF]/60 focus:ring-1 focus:ring-[#00E5FF]/30"
        />
        {dirty && (
          <button
            type="button"
            onClick={() => onSave(v === "" ? null : parseInt(v, 10))}
            data-testid="hist-cycles-save"
            className="inline-flex items-center gap-1 rounded-md brand-gradient text-black px-2 py-1 text-[10px] font-mono uppercase tracking-wider font-semibold hover:opacity-90"
          >
            <Check className="w-3 h-3" strokeWidth={2.4} />
            Save
          </button>
        )}
      </div>
    </div>
  );
};

const PriceRow = ({ value, onSave }) => {
  const [v, setV] = useState(value == null ? "" : String(value));
  useEffect(() => { setV(value == null ? "" : String(value)); }, [value]);
  const dirty = (v || "") !== (value == null ? "" : String(value));
  return (
    <div className="rounded-md border p-2.5" style={{ borderColor: "rgba(255,176,32,0.28)", background: "rgba(255,176,32,0.03)" }} data-testid="hist-price">
      <div className="text-[12px] text-white flex items-center gap-1.5">
        <DollarSign className="w-3 h-3 shrink-0" style={{ color: COLOR.amber }} strokeWidth={2.4} />
        Home electricity price ($/kWh)
      </div>
      <div className="text-[10px] text-zinc-500 leading-snug mt-0.5 mb-1.5">
        Used by the Charging ledger to price sessions that don't specify a rate.
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={v}
          step="0.001"
          min="0"
          max="2"
          placeholder="0.14"
          onChange={(e) => setV(e.target.value)}
          data-testid="hist-price-input"
          className="flex-1 rounded-md border border-white/10 bg-black/60 px-2 py-1 text-[11px] text-white placeholder-zinc-600 focus:outline-none focus:border-[#FFB020]/60 focus:ring-1 focus:ring-[#FFB020]/30"
        />
        {dirty && (
          <button
            type="button"
            onClick={() => onSave(v === "" ? null : parseFloat(v))}
            data-testid="hist-price-save"
            className="inline-flex items-center gap-1 rounded-md brand-gradient text-black px-2 py-1 text-[10px] font-mono uppercase tracking-wider font-semibold hover:opacity-90"
          >
            <Check className="w-3 h-3" strokeWidth={2.4} />
            Save
          </button>
        )}
      </div>
    </div>
  );
};

const OwnerEmailRow = ({ value, notifiedAt, onSave }) => {
  const [v, setV] = useState(value || "");
  useEffect(() => { setV(value || ""); }, [value]);
  const dirty = (v || "").trim().toLowerCase() !== (value || "").trim().toLowerCase();
  const valid = !v || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());
  return (
    <div className="rounded-md border p-2.5" style={{ borderColor: "rgba(57,255,20,0.28)", background: "rgba(57,255,20,0.03)" }} data-testid="hist-owner-email">
      <div className="text-[12px] text-white flex items-center gap-1.5">
        <Mail className="w-3 h-3 shrink-0" style={{ color: COLOR.green }} strokeWidth={2.4} />
        Your email (for Legendary badge notifications)
      </div>
      <div className="text-[10px] text-zinc-500 leading-snug mt-0.5 mb-1.5">
        We'll only email you the moment your pack earns its 90-day preservation badge.
        {notifiedAt && (
          <span className="ml-1" style={{ color: COLOR.green }}>
            · Congrats — you've earned it ({new Date(notifiedAt).toLocaleDateString()})
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="email"
          value={v}
          onChange={(e) => setV(e.target.value)}
          placeholder="you@example.com"
          data-testid="hist-owner-email-input"
          className="flex-1 rounded-md border border-white/10 bg-black/60 px-2 py-1 text-[11px] text-white placeholder-zinc-600 focus:outline-none focus:border-[#39FF14]/60 focus:ring-1 focus:ring-[#39FF14]/30"
        />
        {dirty && valid && (
          <button
            type="button"
            onClick={() => onSave(v.trim().toLowerCase() || null)}
            data-testid="hist-owner-email-save"
            className="inline-flex items-center gap-1 rounded-md brand-gradient text-black px-2 py-1 text-[10px] font-mono uppercase tracking-wider font-semibold hover:opacity-90"
          >
            <Check className="w-3 h-3" strokeWidth={2.4} />
            Save
          </button>
        )}
      </div>
      {!valid && v && (
        <div className="mt-1 text-[10px] text-[#FF6B57]">Please enter a valid email address.</div>
      )}
    </div>
  );
};
